import { CSSProperties, useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Button from '@mui/material/Button';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import ventraLogo from '../assets/ventra-logo-light.png';
import { theme } from 'lib/theme';
import { useAuthentication } from 'hooks/useAuthentication';
import { BrandColor } from 'constants/brandColor';
import { Box } from '@mui/material';

const StyledTabs = styled(Tabs)({
  color: 'white',
  '.MuiTabs-indicator': {
    backgroundColor: `${BrandColor.ACCENT}` /* ventra orange */
  },
  marginLeft: '25px',
  marginRight: '25px',
  flex: '1 0 auto'
});

const Img = styled('img')({
  width: '250px'
});

export interface AppbarProps {
  style?: CSSProperties; // Need to be able to override the display:none when there are no routes
}

function Appbar(props: AppbarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { getUserInfo, logoutNavigate, isLoggedIn } = useAuthentication();
  const user = getUserInfo();
  const [routes, setRoutes] = useState<string[]>([]);

  useEffect(() => {
    const routes = [];
    if (!user?.token) return;
    if (user.showMyEncounters) {
      routes.push('/MyEncounters');
    }
    if (user.showMyTransactions) {
      routes.push('/MyTransactionsSummary');
    }
    if (user.showMyAccount) {
      routes.push('/MyAccount');
    }
    setRoutes(routes);
  }, [user?.token]);
  const value = routes.indexOf(location.pathname);

  const showAppBar =
    value >= 0 ||
    location.pathname === '/ForgotPassword' ||
    location.pathname === '/ResetPassword';

  const displayTabs = !(
    location.pathname === '/ForgotPassword' ||
    location.pathname === '/ResetPassword'
  ); // Don't display tabs when on the forgot password page
  const loginButtonOverride =
    location.pathname === '/ForgotPassword' ||
    location.pathname === '/ResetPassword'; // Force displaying login rather than logout, even if user has valid token

  return (
    <AppBar
      position="static"
      style={{
        //backgroundColor: theme.palette.primary.dark,
        display: showAppBar ? 'flex' : 'none',
        padding: '1em',
        backgroundColor: 'rgb(44, 86, 118)',
        ...props.style
      }}
    >
      <Toolbar style={{ paddingLeft: '0px' }}>
        <Img src={ventraLogo} />
        <Typography variant="h4" style={{ fontWeight: 800, marginLeft: 50 }}>
          Partner Portal
        </Typography>
        {displayTabs && (
          <StyledTabs value={value < 0 ? 0 : value ?? 0} textColor="inherit">
            {/* <Tab label="My Dashboard" disabled/> */}
            {user && user.showMyEncounters && (
              <Tab
                sx={{
                  color: 'white',
                  opacity: 0.7,
                  '&.Mui-active': {
                    opacity: 1
                  }
                }}
                label="My Encounters"
                onClick={() => navigate('/MyEncounters')}
              />
            )}
            {user && user.showMyTransactions && (
              <Tab
                sx={{
                  color: 'white',
                  opacity: 0.7,
                  '&.Mui-active': { opacity: 1 }
                }}
                label="My Transactions"
                onClick={() => navigate('/MyTransactionsSummary')}
              />
            )}
            {user && user.showMyAccount && (
              <Tab
                sx={{
                  color: 'white',
                  opacity: 0.7,
                  '&.Mui-active': { opacity: 1 }
                }}
                label="My Account"
                onClick={() => navigate('/MyAccount')}
              />
            )}
            {/* <Tab label="My Users" disabled/> */}
          </StyledTabs>
        )}
        <div style={{ flexGrow: 1 }} />
        <Box display="flex" flexDirection="row">
          {isLoggedIn() && !loginButtonOverride ? (
            <div style={{ display: 'flex' }}>
              <Typography
                variant="h6"
                style={{
                  textAlign: 'right',
                  paddingRight: '15px',
                  fontSize: '1rem',
                  paddingTop: '7px'
                }}
              >
                {!!user &&
                  !!user.externalUsersName &&
                  `Logged in as: ${user.externalUsersName}`}
              </Typography>
              <Button
                color="primary"
                variant="contained"
                onClick={() => logoutNavigate(navigate)}
              >
                Log Out
              </Button>
            </div>
          ) : (
            <div style={{ display: 'flex' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate('/')}
              >
                Login
              </Button>
            </div>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
}

export default Appbar;
